import { Box, Stack, Alert, Button, EmptyState, Skeleton } from '@citibox-panel/ui';
import type { LockerLog } from '@citibox-panel/api-client';
import { useLockerLog } from '../hooks/useLockerLog';
import { LockerLogTable } from './LockerLogTable';

export interface LockerLogSectionProps {
  open: boolean;
  boxId: string;
  fetchLockerLog: (boxId: string) => Promise<LockerLog>;
}

// Mounted whenever the delivery has a «buzón» (even collapsed), so the query keeps its cache and
// observer across a collapse→expand; the hook is open-gated, so opening the detail costs no
// request and the first unfold is the lazy fetch. Renders nothing until unfolded. Owns the
// loading/empty/error/success states; a fetch failure stays inline (retry) so the rest of the
// «Entrega» detail keeps working. Rows carry «Usuario» PII — rendered, never logged.
export function LockerLogSection({ open, boxId, fetchLockerLog }: LockerLogSectionProps) {
  // The hook runs before the early return, so it stays mounted while this component is (the
  // query is skipped until `open`). The early return only hides the visual region when collapsed.
  const { log, isPending, isError, refetch } = useLockerLog(boxId, open, fetchLockerLog);
  if (!open) return null;
  return (
    <Box sx={{ px: 2, pt: 2, pb: 2 }}>
      {isPending ? (
        <Stack spacing={1}>
          {Array.from({ length: 5 }).map((_, i) => (
            <Skeleton key={i} width="100%" height={32} />
          ))}
        </Stack>
      ) : isError ? (
        <Box>
          {/* TODO: i18n */}
          <Alert severity="error">No se ha podido cargar el log del buzón.</Alert>
          <Box sx={{ mt: 1 }}>
            <Button variant="text" onClick={refetch}>
              Reintentar
            </Button>
          </Box>
        </Box>
      ) : log === undefined || log.events.length === 0 ? (
        // TODO: i18n
        <EmptyState title="Sin registros para este buzón." />
      ) : (
        <LockerLogTable log={log} />
      )}
    </Box>
  );
}
